import React, { useEffect, useState } from "react";
import { Box, Typography, Grid, Card, CardContent, CardActionArea, Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import FavoriteIcon from "@mui/icons-material/Favorite";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LabelList, CartesianGrid } from "recharts";
import { collection, getDocs } from "firebase/firestore";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { db } from "../firebase";
import "../css/EstadisticasRecetas.css";

const EstadisticasRecetas = () => {
    const [recetas, setRecetas] = useState<any[]>([]);

    useEffect(() => {
        const fetchRecetas = async () => {
            const snapshot = await getDocs(collection(db, "recetas"));
            const data = snapshot.docs.map((doc) => doc.data());
            setRecetas(data);
        };
        fetchRecetas();
        const root = document.getElementById("root");
        if (root) {
            root.scrollIntoView({ behavior: "auto" });
        }
    }, []);

    const total = recetas.length;
    const favoritas = recetas.filter((r) => r.isFavorite).length;
    const categorias = [...new Set(recetas.map((r) => r.categoria))];

    const porCategoria = categorias.map((cat) => ({
        name: cat,
        value: recetas.filter((r) => r.categoria === cat).length,
    }));

    const getBadgeColor = (categoria: string): string => {
        const colores: { [key: string]: string } = {
            "Pasta": "#f4a261",
            "Pizza": "#e76f51",
            "Pescado": "#00b4d8",
            "Carnes": "#b56576",
            "Mariscos": "#2a9d8f",
            "Verduras": "#81b29a",
            "Legumbres": "#6a994e",
            "Hamburguesas": "#ef476f",
            "Arroz": "#ffd166",
            "Sopas": "#f5cac3",
            "Ensaladas": "#a7c957",
            "Postres": "#ffb4a2",
            "Repostería": "#ff9f1c",
            "Aperitivos": "#c77dff",
            "Sandwiches": "#ff6d00",
            "Desayunos": "#f7b801",
            "Smoothies": "#9b5de5",
            "Comida Internacional": "#06d6a0",
            "Otros": "#adb5bd",
        };

        return colores[categoria] || "#cccccc";
    };

    return (
         <div className="container-fluid">
            <div className="text-center">
                <h2>Estadísticas Recetas</h2>
            </div>
            <div className="container-fluid content py-3 mb-4">
                <Grid container spacing={2}>

                    <Grid item xs={6} md={3}>
                        <Card
                            sx={{
                                boxShadow: 5,
                                borderRadius: 2,
                                transition: 'transform 0.15s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    boxShadow: 8,
                                },
                            }}
                        >
                            <CardActionArea> {/* onClick={() => console.log('Total recetas')} */}
                                <CardContent sx={{ pb: 1.5 }}>
                                    <Typography variant="h6">Total recetas</Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h4">{total}</Typography>
                                        <RestaurantIcon color="primary" sx={{ fontSize: 28 }} />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <Card
                            sx={{
                                boxShadow: 5,
                                borderRadius: 2,
                                transition: 'transform 0.15s ease-in-out',
                                '&:hover': {
                                    transform: 'scale(1.03)',
                                    boxShadow: 8,
                                },
                            }}
                        >
                            <CardActionArea> {/* onClick={() => console.log('Favoritas')} */}
                                <CardContent sx={{ pb: 1.5 }}>
                                    <Typography variant="h6">Favoritas</Typography>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Typography variant="h4">{favoritas}</Typography>
                                        <FavoriteIcon color="error" sx={{ fontSize: 28 }} />
                                    </Box>
                                </CardContent>
                            </CardActionArea>
                        </Card>
                    </Grid>


                    <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                            <Pie
                                data={porCategoria}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={100}
                                fill="#8884d8"
                                label={({ name, percent }) =>
                                    window.innerWidth > 600
                                        ? name
                                        : `${(percent * 100).toFixed(0)}%`
                                }
                            >
                                {porCategoria.map((entry, index) => (
                                    <Cell
                                        key={`cell-${index}`}
                                        fill={getBadgeColor(entry.name)}
                                    />
                                ))}
                            </Pie>
                            <Tooltip />
                        </PieChart>
                    </ResponsiveContainer>

                    <Box sx={{ width: "100vw", mx: "auto", mt: 2, pl: 2 }}>
                        <Accordion elevation={3} sx={{ borderRadius: 2 }}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="panel-bar-chart-content"
                                id="panel-bar-chart-header"
                                sx={{
                                    backgroundColor: "#f5f5f5",
                                    fontWeight: "bold",
                                    color: "var(--primary-brand-color)",
                                    borderRadius: 2,
                                }}
                            >
                                Ver gráfico por categorías
                            </AccordionSummary>
                            <AccordionDetails sx={{ padding: 0 }}>
                                <ResponsiveContainer width="100%" height={400}>
                                    <BarChart
                                        data={porCategoria}
                                        margin={{ top: 20, right: 25, bottom: 60 }}
                                    >
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis
                                            dataKey="name"
                                            interval={0}
                                            angle={-45}
                                            textAnchor="end"
                                            height={80}
                                        />
                                        <YAxis />
                                        <Tooltip />
                                        <Bar dataKey="value">
                                            {porCategoria.map((entry, index) => (
                                                <Cell
                                                    key={`cell-bar-${index}`}
                                                    fill={getBadgeColor(entry.name)}
                                                />
                                            ))}
                                            <LabelList dataKey="value" position="top" fontSize={10} />
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </AccordionDetails>
                        </Accordion>
                    </Box>


                </Grid>
            </div>
        </div>
    );
};

export default EstadisticasRecetas;
